import { ComponentsManager, Component, EventBus } from '../core/smpl-components.js'

import { gsap } from 'gsap'

class VideoPlayerController {
	constructor(options) {
		this.options = {
			playOnCoverClick: true,
			coverFadeDuration: 0.5,
			restoreOnLeave: true,
			...options,
		}

		this.elVideo = options.elVideo
		this.elButton = options.elButton
		this.elCover = options.elCover

		this.isLoading = false
		this.hasStarted = false

		this.bndBtnClick = this.onBtnClick.bind(this)
		this.bndPlaying = this.onPlaying.bind(this)

		if (this.options.playOnCoverClick && this.elCover) {
			this.elCover.addEventListener('click', this.bndBtnClick)
		}
		if (this.elButton) {
			this.elButton.addEventListener('click', this.bndBtnClick)
		}

		if (this.elVideo) {
			this.elVideo.addEventListener('playing', this.bndPlaying)
		}

		this.setupObserver()
	}

	onBtnClick(ev) {
		if (ev) {
			ev.preventDefault()
			ev.stopPropagation()
		}

		if (this.hasStarted || this.isLoading || !this.elVideo) {
			return
		}

		this.isLoading = true
		this.disablePlayUi()

		// native video: unmute so user-initiated playback has sound
		this.elVideo.muted = false
		this.elVideo.loop = false

		const playPromise = this.elVideo.play()
		if (playPromise && typeof playPromise.catch === 'function') {
			playPromise.catch(() => {
				// autoplay/play rejected — fall back to muted playback
				this.elVideo.muted = true
				this.elVideo.play().catch(() => {
					this.isLoading = false
					this.enablePlayUi()
				})
			})
		}
	}

	onPlaying() {
		if (this.hasStarted) {
			return
		}
		this.revealVideo()
	}

	disablePlayUi() {
		if (this.elButton) {
			this.elButton.disabled = true
			this.elButton.setAttribute('aria-busy', 'true')

			gsap.to(this.elButton, { opacity: 0, duration: 0.2, ease: 'power3.out' })
		}
	}

	enablePlayUi() {
		if (this.elButton) {
			this.elButton.disabled = false
			this.elButton.removeAttribute('aria-busy')

			gsap.to(this.elButton, { opacity: 1, duration: 0.2, delay: 0.5, ease: 'power3.out' })
		}
	}

	revealVideo() {
		this.hasStarted = true
		this.isLoading = false
		this.enablePlayUi()

		if (!this.elCover) {
			return
		}

		gsap.to(this.elCover, {
			opacity: 0,
			duration: this.options.coverFadeDuration,
			onComplete: () => {
				this.elCover.style.display = 'none'
			},
		})
	}

	// Pause + bring cover back when scrolled out of view
	setupObserver() {
		if (!this.options.restoreOnLeave || !('IntersectionObserver' in window)) {
			return
		}

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting && this.hasStarted) {
						this.reset({ showCover: true })
					}
				})
			},
			{ threshold: 0 },
		)

		this.observer.observe(this.elVideo)
	}

	reset({ showCover = true } = {}) {
		if (this.elVideo) {
			this.elVideo.pause()
			this.elVideo.currentTime = 0
			this.elVideo.muted = true
		}

		this.hasStarted = false
		this.isLoading = false
		this.enablePlayUi()

		if (showCover && this.elCover) {
			this.elCover.style.display = ''
			gsap.set(this.elCover, { opacity: 1 })
		}
	}

	destroy() {
		if (this.observer) {
			this.observer.disconnect()
			this.observer = null
		}

		if (this.elVideo) {
			this.elVideo.removeEventListener('playing', this.bndPlaying)
		}

		if (this.elButton) {
			this.elButton.removeEventListener('click', this.bndBtnClick)
		}

		if (this.options.playOnCoverClick && this.elCover) {
			this.elCover.removeEventListener('click', this.bndBtnClick)
		}
	}
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {
		const elVideo = this.element.querySelector('.js-external-video')
		const elCover = this.element.querySelector('.js-external-video-player-cover')
		const elButton = this.element.querySelector('.js-external-video-player-play')
		if (elVideo) {
			this.videoPlayerController = new VideoPlayerController({
				elVideo: elVideo,
				elButton: elButton,
				elCover: elCover,
			})
		}
	}
	mount() {}
	unmount() {
		if (this.videoPlayerController) {
			this.videoPlayerController.destroy()
			this.videoPlayerController = null
		}
	}

	resetVideo() {
		if (this.videoPlayerController) {
			this.videoPlayerController.reset()
		}
	}
}
